import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { Rocket, Handshake, Briefcase, Coins, Mail, Lock, EyeOff, Eye, Ban, XCircle, AlertTriangle, Clock, RefreshCw, CheckCircle, Sparkles, Globe, Zap, BookOpen } from 'lucide-react';

const Login = () => {
  const { login, resendVerificationEmail } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [mot_de_passe, setMotDePasse] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const navigate = useNavigate();

  // Vérifier s'il y a un message d'erreur de compte suspendu/supprimé
  useEffect(() => {
    const accountError = localStorage.getItem('account_error');
    if (accountError) {
      setError(accountError);
      localStorage.removeItem('account_error');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setEmailNotVerified(false);
    setResendSuccess(false);
    
    try {
      await login(email, mot_de_passe);
      navigate('/');
    } catch (err) {
      // Vérifier si c'est une erreur d'email non vérifié
      if (err.response?.data?.email_not_verified) {
        setEmailNotVerified(true);
        setUnverifiedEmail(err.response.data.email);
        setError('Veuillez vérifier votre adresse email avant de vous connecter.');
      } else {
        // Récupérer le message d'erreur du serveur (compte suspendu, supprimé, etc.)
        const serverError = err.response?.data?.error;
        setError(serverError || 'Identifiants invalides');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await resendVerificationEmail(unverifiedEmail || email);
      setResendSuccess(true);
    } catch (err) {
      setError('Erreur lors de l\'envoi. Réessayez plus tard.');
    } finally {
      setResendLoading(false);
    }
  };

  const features = [
    { icon: <Rocket className="text-white" size={24} />, title: 'Lancez votre business', desc: 'Démarrez votre activité même sans capital, ni matériel' },
    { icon: <Handshake className="text-white" size={24} />, title: 'Trouvez des partenaires', desc: 'Connectez-vous avec des talents prêts à collaborer' },
    { icon: <Briefcase className="text-white" size={24} />, title: 'Offrez vos services', desc: 'Monétisez vos compétences et trouvez des clients' },
    { icon: <Coins className="text-white" size={24} />, title: 'Financements partagés', desc: 'Réalisez des projets grâce au financement collectif' },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding & Features */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-60 h-60 bg-white rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-32 h-32 bg-white rounded-full blur-2xl"></div>
        </div>

        <div className="relative z-10">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-16">
            <img 
              src="/logo.png" 
              alt="DJAMANN" 
              className="w-16 h-16 rounded-2xl shadow-2xl object-contain bg-white/10 backdrop-blur-sm p-1"
            />
            <div>
              <h1 className="text-3xl font-bold text-white">DJAMANN</h1>
              <p className="text-white/70 text-sm">Lancez votre business, même avec zéro franc</p>
            </div>
          </div>

          {/* Main Heading */}
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
              Lancez votre business <br />
              <span className="text-yellow-300">même sans moyens</span>
            </h2>
            <p className="text-white/80 text-lg max-w-md">
              Pas de capital ? Pas de matériel ? Pas de problème ! Trouvez des partenaires, partagez les ressources et construisez ensemble votre succès.
            </p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all duration-300"
              >
                <span className="text-3xl mb-2 block">{feature.icon}</span>
                <h3 className="text-white font-semibold mb-1">{feature.title}</h3>
                <p className="text-white/70 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-white/60 text-sm">
            © 2025 DJAMANN. La plateforme qui démocratise l'entrepreneuriat.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="/logo.png" 
              alt="DJAMANN" 
              className="w-20 h-20 mx-auto mb-4 rounded-2xl shadow-lg object-contain"
            />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              DJAMANN
            </h1>
            <p className="text-gray-500 mt-1">Lancez votre business, même avec zéro franc</p>
          </div>

          {/* Welcome Text */}
          <div className="text-center mb-8">
            <p className="text-gray-500">Connectez-vous pour gérer vos projets et collaborations</p>
          </div>

          {/* Login Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail size={16} className="inline mr-1" /> Adresse email
                </label>
                <input
                  type="email"
                  placeholder="nom@exemple.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition-all bg-gray-50 hover:bg-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Lock size={16} className="inline mr-1" /> Mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={mot_de_passe}
                    onChange={e => setMotDePasse(e.target.value)}
                    required
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition-all bg-gray-50 hover:bg-white pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500" 
                  />
                  <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                    Se souvenir de moi
                  </span>
                </label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                >
                  Mot de passe oublié ?
                </Link>
              </div>

              {error && (
                <div className={`p-4 border rounded-xl flex items-start gap-3 ${
                  emailNotVerified
                    ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                    : error.includes('suspendu') 
                    ? 'bg-orange-50 border-orange-200 text-orange-800'
                    : error.includes('supprimé')
                    ? 'bg-gray-100 border-gray-300 text-gray-700'
                    : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                  <span className="text-xl flex-shrink-0">
                    {emailNotVerified ? <Mail size={20} /> : error.includes('suspendu') ? <Ban size={20} /> : error.includes('supprimé') ? <XCircle size={20} /> : <AlertTriangle size={20} />}
                  </span>
                  <div className="flex-1">
                    <span className="text-sm font-medium block">{error}</span>
                    
                    {/* Bouton renvoyer email de vérification */}
                    {emailNotVerified && !resendSuccess && (
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={resendLoading}
                        className="text-xs underline mt-2 inline-block hover:opacity-80 text-yellow-700 disabled:opacity-50"
                      >
                        {resendLoading ? <><Clock size={14} className="inline mr-1" /> Envoi en cours...</> : <><RefreshCw size={14} className="inline mr-1" /> Renvoyer l'email de vérification</>}
                      </button>
                    )}
                    
                    {/* Message de succès après renvoi */}
                    {resendSuccess && (
                      <span className="text-xs text-green-600 mt-2 block">
                        <CheckCircle size={14} className="inline mr-1" /> Email envoyé ! Vérifiez votre boîte mail (et les spams).
                      </span>
                    )}
                    
                    {(error.includes('suspendu') || error.includes('supprimé')) && (
                      <a 
                        href="mailto:support@djamann.com" 
                        className="text-xs underline mt-2 inline-block hover:opacity-80"
                      >
                        <Mail size={14} className="inline mr-1" /> Contacter le support
                      </a>
                    )}
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg shadow-indigo-200 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Connexion en cours...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Se connecter
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-400">Nouveau sur DJAMANN ?</span>
              </div>
            </div>

            {/* Register Link */}
            <Link
              to="/register"
              className="w-full py-3.5 border-2 border-indigo-200 text-indigo-600 font-semibold rounded-xl hover:bg-indigo-50 hover:border-indigo-300 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Sparkles size={18} />
              Créer un compte gratuitement
            </Link>
          </div>

          {/* Trust Badges */}
          <div className="mt-8 flex items-center justify-center gap-6 text-gray-400 text-sm">
            <div className="flex items-center gap-1">
              <Lock size={14} /> Sécurisé
            </div>
            <div className="flex items-center gap-1">
              <Zap size={14} /> Rapide
            </div>
            <div className="flex items-center gap-1">
              <Globe size={14} /> Mondial
            </div>
          </div>

          {/* Guide Link */}
          <div className="mt-6 text-center">
            <Link 
              to="/guide" 
              className="inline-flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors text-sm"
            >
              <BookOpen size={16} />
              <span>Découvrir le guide d'utilisation</span>
            </Link>
          </div>


        </div>
      </div>
    </div>
  );
};

export default Login;
